import React from "react";
import { Box, Link, Typography } from "@mui/material";

/**
 * Hand-rolled, minimal markdown renderer. We support exactly what Kate is
 * likely to emit — paragraphs, fenced code blocks, inline code, bold,
 * `![alt](data:image/png;base64,...)` images, and `[text](url)` links —
 * and intentionally avoid pulling in react-markdown to keep the bundle small
 * and avoid React 17 peer-dep headaches.
 */

type CodeAwareBlock =
  | { kind: "text"; value: string }
  | { kind: "code"; language: string; value: string };

type InlineBlock =
  | { kind: "text"; value: string }
  | { kind: "image"; alt: string; src: string };

const splitIntoBlocks = (content: string): CodeAwareBlock[] => {
  const blocks: CodeAwareBlock[] = [];
  const codeFence = /```([a-zA-Z0-9_-]*)\n([\s\S]*?)```/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = codeFence.exec(content)) !== null) {
    if (match.index > cursor) {
      blocks.push({ kind: "text", value: content.slice(cursor, match.index) });
    }
    blocks.push({ kind: "code", language: match[1] || "", value: match[2] });
    cursor = match.index + match[0].length;
  }
  if (cursor < content.length) {
    blocks.push({ kind: "text", value: content.slice(cursor) });
  }
  return blocks;
};

const extractImages = (text: string): InlineBlock[] => {
  const out: InlineBlock[] = [];
  const imageRegex = /!\[([^\]]*)\]\((data:image\/[^)]+)\)/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = imageRegex.exec(text)) !== null) {
    if (match.index > cursor) {
      out.push({ kind: "text", value: text.slice(cursor, match.index) });
    }
    out.push({ kind: "image", alt: match[1] || "image", src: match[2] });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) {
    out.push({ kind: "text", value: text.slice(cursor) });
  }
  return out;
};

const renderInline = (text: string, key: string): React.ReactNode => {
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  const boldRegex = /\*\*([^*]+)\*\*/g;
  const inlineCodeRegex = /`([^`]+)`/g;

  // Pass 1: links
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = linkRegex.exec(text)) !== null) {
    if (m.index > cursor) parts.push(text.slice(cursor, m.index));
    parts.push(
      <Link
        key={`${key}-l-${i++}`}
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
      >
        {m[1]}
      </Link>,
    );
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  // Pass 2: bold + inline code on the string parts
  const flatten = (nodes: React.ReactNode[]): React.ReactNode[] =>
    nodes.flatMap((node, idx) => {
      if (typeof node !== "string") return [node];
      const out: React.ReactNode[] = [];
      let c = 0;
      let mm: RegExpExecArray | null;
      while ((mm = boldRegex.exec(node)) !== null) {
        if (mm.index > c) out.push(node.slice(c, mm.index));
        out.push(<strong key={`${key}-b-${idx}-${c}`}>{mm[1]}</strong>);
        c = mm.index + mm[0].length;
      }
      if (c < node.length) out.push(node.slice(c));
      return out;
    });

  const flatten2 = (nodes: React.ReactNode[]): React.ReactNode[] =>
    nodes.flatMap((node, idx) => {
      if (typeof node !== "string") return [node];
      const out: React.ReactNode[] = [];
      let c = 0;
      let mm: RegExpExecArray | null;
      while ((mm = inlineCodeRegex.exec(node)) !== null) {
        if (mm.index > c) out.push(node.slice(c, mm.index));
        out.push(
          <Box
            key={`${key}-c-${idx}-${c}`}
            component="code"
            sx={{
              fontFamily: "monospace",
              backgroundColor: "rgba(0,0,0,0.06)",
              px: 0.6,
              py: 0.1,
              borderRadius: 0.5,
              fontSize: "0.9em",
            }}
          >
            {mm[1]}
          </Box>,
        );
        c = mm.index + mm[0].length;
      }
      if (c < node.length) out.push(node.slice(c));
      return out;
    });

  return flatten2(flatten(parts));
};

export const MessageRenderer: React.FC<{ content: string }> = ({ content }) => {
  const codeAware = splitIntoBlocks(content);

  return (
    <Box>
      {codeAware.map((block, i) => {
        if (block.kind === "code") {
          return (
            <Box
              key={`code-${i}`}
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                backgroundColor: "#1e1e2e",
                color: "#f5f5f5",
                p: 1.5,
                my: 1,
                borderRadius: 1,
                overflow: "auto",
                whiteSpace: "pre",
              }}
            >
              {block.value}
            </Box>
          );
        }

        const subBlocks = extractImages(block.value);
        return (
          <Box key={`text-${i}`}>
            {subBlocks.map((sb, j) => {
              if (sb.kind === "image") {
                return (
                  <Box
                    key={`img-${i}-${j}`}
                    component="img"
                    src={sb.src}
                    alt={sb.alt}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 320,
                      display: "block",
                      my: 1,
                      borderRadius: 1,
                      border: "1px solid rgba(0,0,0,0.1)",
                      cursor: "zoom-in",
                    }}
                    onClick={() => window.open(sb.src, "_blank")}
                  />
                );
              }
              const paragraphs = sb.value.split(/\n\n+/);
              return (
                <React.Fragment key={`txt-${i}-${j}`}>
                  {paragraphs.map((p, k) => (
                    <Typography
                      key={`p-${i}-${j}-${k}`}
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        my: 0.75,
                      }}
                    >
                      {renderInline(p, `inline-${i}-${j}-${k}`)}
                    </Typography>
                  ))}
                </React.Fragment>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};
